import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customerAPI, broadbandAPI, catvAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  UserPlus, ArrowLeft, ScanLine, Tv, Wifi, CheckCircle, XCircle, Loader, Receipt, Paperclip, X,
  Check, User, MapPin, ClipboardList, BadgeCheck, ChevronLeft, ChevronRight, Layers,
} from 'lucide-react';
import AddableSelect from '../components/common/AddableSelect';
import ISPVlanSelect from '../components/common/ISPVlanSelect';
import SearchableSelect from '../components/common/SearchableSelect';
import UniqueField from '../components/common/UniqueField';
import useDarkReady from '../theme/useDarkReady';

// ── CATV expiry calculator ────────────────────────────────────────────────────
function calcCatvExpiry(catvType, dtdMonths, baseDate) {
  const d = baseDate ? new Date(baseDate) : new Date();
  if (catvType === 'regular') {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0); // last day of that month
  }
  const exp = new Date(d);
  exp.setDate(exp.getDate() + (dtdMonths || 3) * 30);
  return exp;
}

function fmtExpiry(date) {
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const SERVICE_OPTIONS = [
  {
    key: 'catv',
    label: 'CableTV',
    icon: Tv,
    // Real hex (not var()) — concatenated with alpha-suffix digits elsewhere
    // (AddableSelect's themeColor prop), which only works with a literal hex.
    color: '#1e3a8a',
    colorLight: 'var(--cb-dbeafe)',
    colorMid: 'var(--cb-bfdbfe)',
    desc: 'CableTV only — STB & package',
  },
  {
    key: 'broadband',
    label: 'Broadband',
    icon: Wifi,
    color: '#1e40af',
    colorLight: 'var(--cb-e0f2fe)',
    colorMid: 'var(--cb-bae6fd)',
    desc: 'Broadband only — internet plan & PPPoE',
  },
];

// Document types staff can attach at signup — some apply to both services, some are
// service-specific (STB Image is CableTV-only; Signature/Visiting Card are Broadband-only,
// Visiting Card being for shop/business connections specifically).
const DOCUMENT_TYPES = [
  { key: 'aadhar',        label: 'Aadhar Card',           services: ['catv', 'broadband'] },
  { key: 'light_bill',    label: 'Light Bill',            services: ['catv', 'broadband'] },
  { key: 'photo',         label: 'Passport-size Photo',   services: ['catv', 'broadband'] },
  { key: 'stb_image',     label: 'STB Image',              services: ['catv'] },
  { key: 'signature',     label: 'Signature',              services: ['broadband'] },
  { key: 'visiting_card', label: 'Visiting Card (shop/business)', services: ['broadband'] },
];

// Wizard steps — the form used to be one long two-column scroll; broken into a
// step-by-step flow (progress rail, back/continue, a final Review-and-edit step)
// so a long signup doesn't feel like a wall of fields. Every field/validation rule
// below is unchanged from the single-page version, just regrouped.
const STEPS = [
  { key: 'type',      label: 'Service Type', icon: Layers },
  { key: 'personal',  label: 'Personal Info', icon: User },
  { key: 'address',   label: 'Address', icon: MapPin },
  { key: 'service',   label: 'Service Details', icon: Receipt },
  { key: 'billing',   label: 'Billing', icon: ClipboardList },
  { key: 'documents', label: 'Documents', icon: Paperclip },
  { key: 'review',    label: 'Review', icon: BadgeCheck },
];

export default function AddCustomerPage() {
  useDarkReady();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledStb = searchParams.get('stb') || '';
  const fromScan     = Boolean(prefilledStb);

  const [loading, setLoading] = useState(false);
  const [plans, setPlans]     = useState([]);
  const [packages, setPackages] = useState([]);
  const [nodeOptions, setNodeOptions] = useState([]);
  const [stbModelOptions, setStbModelOptions] = useState([]);
  const [entityCodeOptions, setEntityCodeOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [tab, setTab] = useState('personal');
  const [documents, setDocuments] = useState({}); // key -> File, staged until after customer create

  // Wizard step position — furthestValid lets a step already completed be revisited
  // (rail click / Review "Edit" links) without losing progress on later steps.
  const [stepIndex, setStepIndex] = useState(0);
  const [furthestValid, setFurthestValid] = useState(0);

  // Unique field states exposed for submit-blocking
  const [pppoeStatus, setPppoeStatus] = useState('idle');
  const [acctStatus, setAcctStatus] = useState('idle');
  const [billingCheck, setBillingCheck] = useState({ status: 'idle', message: '' });
  const billingDebounce = useRef(null);
  const lastAutoBilling = useRef(''); // last prefilled sequence number — overwrite only if untouched

  const [form, setForm] = useState({
    name: '', billingName: '', mobile: '', alternateMobile: '', email: '',
    billingNumber: '',
    customerType: fromScan ? 'catv' : '',
    address: { houseNumber: '', line1: '', area: '', city: 'Ahmedabad', coordinates: { lat: '', lng: '' } },
    billingAddress: { sameAsInstallation: true, line1: '', city: '', pincode: '' },
    installationDate: new Date().toISOString().split('T')[0],
    broadband: { plan: '', additionalPlanIds: [], houseNumber: '', username: '', routerModel: '', routerSerial: '', ispName: '', autoRenew: false },
    catv: { package: '', additionalPackageIds: [], houseNumber: '', stbModel: '', setTopBoxSerial: prefilledStb, entityCode: '', accountNumber: '', node: '' },
    billing: {
      startDate: (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]; })(),
      openingBalance: 0,
      openingBalanceType: 'outstanding',
      monthlyAdjustment: 0,
      monthlyAdjustmentType: 'charge',
      durationType: 'regular',
      durationDays: 30,
      billType: 'prepaid',
      gstType: 'no_gst',
      gstNumber: '',
    },
    securityDeposit: 0, notes: '', createPortalLogin: true,
  });

  useEffect(() => {
    broadbandAPI.getPlans().then(({ data }) => setPlans(data.data)).catch(() => {});
    catvAPI.getPackages().then(({ data }) => setPackages(data.data)).catch(() => {});
    // Every node value already assigned to a real customer — merged into the Node
    // AddableSelect below so it isn't limited to whatever's been manually added via "+".
    customerAPI.getNodes().then(({ data }) => setNodeOptions(data.data || [])).catch(() => {});
    // Same reasoning as nodes above — every STB model / entity code already used on a real
    // customer record, not just whatever's been manually added via "+".
    customerAPI.getStbModels().then(({ data }) => setStbModelOptions(data.data || [])).catch(() => {});
    customerAPI.getEntityCodes().then(({ data }) => setEntityCodeOptions(data.data || [])).catch(() => {});
  }, []);

  // Societies already used by real customers of the selected service type — merged into
  // the Society AddableSelect below so switching CableTV/Broadband scopes the list instead
  // of showing every society across both service types.
  useEffect(() => {
    if (!form.customerType) { setAreaOptions([]); return; }
    customerAPI.getAreas({ customerType: `${form.customerType},both` })
      .then(({ data }) => setAreaOptions(data.data || []))
      .catch(() => {});
  }, [form.customerType]);

  const set = (path, value) => {
    setForm(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]] = { ...obj[keys[i]] };
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleISPChange = ({ ispName }) => {
    setForm(prev => ({ ...prev, broadband: { ...prev.broadband, ispName } }));
  };

  // Billing number live availability check — pool depends on customerType
  // (CableTV and Broadband each keep their own billing-number series)
  const checkBillingNumber = (val, customerType) => {
    setBillingCheck({ status: 'idle', message: '' });
    clearTimeout(billingDebounce.current);
    const trimmed = String(val).trim();
    if (!trimmed || !customerType) return;
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
      setBillingCheck({ status: 'invalid', message: 'Enter a number like 2 or 2.1' });
      return;
    }
    setBillingCheck({ status: 'checking', message: '' });
    billingDebounce.current = setTimeout(async () => {
      try {
        const { data } = await customerAPI.checkBillingNumber(trimmed, customerType);
        if (data.available) {
          setBillingCheck({ status: 'ok', message: `Available in ${customerType === 'catv' ? 'CableTV' : 'Broadband'}` });
        } else {
          const c = data.conflict;
          setBillingCheck({
            status: 'taken',
            message: `Used by ${c?.name || 'another customer'} (${c?.customerId || ''})`,
            taken: trimmed,
            suggestions: data.suggestions || null,
          });
        }
      } catch { setBillingCheck({ status: 'idle', message: '' }); }
    }, 600);
  };

  const handleBillingNumberChange = (val) => {
    set('billingNumber', val);
    checkBillingNumber(val, form.customerType);
  };

  // When the service type is picked/switched: prefill the next number in that pool's
  // sequence (unless the user typed their own), or re-validate a manual entry against
  // the newly selected pool.
  useEffect(() => {
    if (!form.customerType) return;
    const current = form.billingNumber.trim();
    const untouched = !current || current === lastAutoBilling.current;
    if (untouched) {
      customerAPI.nextBillingNumber(form.customerType)
        .then(({ data }) => {
          const next = String(data.data.next);
          lastAutoBilling.current = next;
          set('billingNumber', next);
          setBillingCheck({ status: 'ok', message: `Next in ${form.customerType === 'catv' ? 'CableTV' : 'Broadband'} sequence` });
        })
        .catch(() => {});
    } else {
      checkBillingNumber(current, form.customerType);
    }
  }, [form.customerType]); // eslint-disable-line

  const showBB    = form.customerType === 'broadband' || form.customerType === 'both';
  const showCATV  = form.customerType === 'catv'      || form.customerType === 'both';

  // Per-step validation — mirrors what handleSubmit already checked before creating,
  // just surfaced earlier (on "Continue") instead of only at final submit.
  const validateStep = (key) => {
    if (key === 'type') {
      if (!form.customerType) return 'Select a customer type to continue';
    }
    if (key === 'personal') {
      if (!form.name.trim()) return 'Full name is required';
      if (!form.mobile.trim()) return 'Mobile is required';
      if (!form.installationDate) return 'Installation date is required';
    }
    if (key === 'address') {
      if (!form.address.houseNumber.trim()) return 'House / Flat No. is required';
      if (!form.address.area.trim()) return 'Society is required';
    }
    if (key === 'service') {
      if (billingCheck.status === 'taken') return 'Billing number already in use';
      if (billingCheck.status === 'invalid') return 'Billing number must be a number like 2 or 2.1';
      if (billingCheck.status === 'checking') return 'Still checking billing number…';
      if (showCATV) {
        if (!form.catv.accountNumber.trim()) return 'Account number is required for CableTV — enter it manually';
        if (acctStatus === 'taken') return 'Account number already in use';
        if (acctStatus === 'checking') return 'Still checking account number…';
      }
      if (showBB) {
        if (pppoeStatus === 'taken') return 'PPPoE username already in use';
        if (pppoeStatus === 'checking') return 'Still checking PPPoE username…';
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step.key);
    if (err) { toast.error(err); return; }
    if (stepIndex < STEPS.length - 1) {
      const next = stepIndex + 1;
      setStepIndex(next);
      setFurthestValid(m => Math.max(m, next));
    }
  };

  const goBack = () => setStepIndex(i => Math.max(0, i - 1));

  const goToStep = (i) => { if (i <= furthestValid) setStepIndex(i); };

  const handleSubmit = async () => {
    if (pppoeStatus === 'taken')   { toast.error('PPPoE username already in use'); return; }
    if (pppoeStatus === 'checking') { toast.error('Checking PPPoE username…'); return; }
    if (billingCheck.status === 'taken')    { toast.error('Billing number already in use'); return; }
    if (billingCheck.status === 'invalid')  { toast.error('Billing number must be a number like 2 or 2.1'); return; }
    if (billingCheck.status === 'checking') { toast.error('Checking billing number…'); return; }
    if (form.customerType === 'catv') {
      if (!form.catv.accountNumber.trim()) { toast.error('Account number is required — enter it manually'); return; }
      if (acctStatus === 'taken')          { toast.error('Account number already in use'); return; }
      if (acctStatus === 'checking')       { toast.error('Checking account number…'); return; }
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!String(form.billingNumber).trim()) delete payload.billingNumber; // blank → auto-assign next in pool
      if (!payload.billingName?.trim()) delete payload.billingName;
      payload.address = {
        ...form.address,
        line1: [form.address.area, form.address.houseNumber].filter(Boolean).join(' '),
        coordinates: (form.address.coordinates.lat !== '' && form.address.coordinates.lng !== '')
          ? { lat: Number(form.address.coordinates.lat), lng: Number(form.address.coordinates.lng) }
          : undefined,
      };
      payload.billing = {
        ...form.billing,
        gstNumber: form.billing.gstType !== 'no_gst' ? (form.billing.gstNumber?.trim() || undefined) : undefined,
      };
      payload.billingAddress = form.billingAddress.sameAsInstallation
        ? { sameAsInstallation: true, line1: payload.address.line1, city: form.address.city, pincode: form.address.pincode || '' }
        : { ...form.billingAddress, sameAsInstallation: false };
      if (form.customerType === 'catv') delete payload.broadband;
      if (form.customerType === 'broadband') delete payload.catv;
      if (payload.catv) {
        payload.catv = {
          ...payload.catv,
          // Mirrors the address step's House/Flat No. — the CATV list/arrangement sort
          // reads catv.houseNumber, not address.line1, so without this it stays blank
          // forever and the customer never lands in the right sequence for its society.
          houseNumber: form.address.houseNumber,
          expiryDate: catvExpiry.date.toISOString(),
          additionalPackageIds: (payload.catv.additionalPackageIds || []).filter(Boolean),
          // catv.catvType and billing.durationType both mean the same thing for a CATV
          // customer (see CustomerDetailPage.js's setDurationType comment) — this wizard
          // only ever collected the choice via the Billing step's durationType radio, so
          // without this catv.catvType silently stayed at the schema default 'regular'
          // even when DTD/AMP was picked. Billing itself was never affected (everything
          // reads billing.durationType), but the CableTV section's own "Type" display did
          // wrongly show Regular. Fixed 2026-09-02 — see 8 pre-existing customers backfilled.
          catvType: payload.billing.durationType,
        };
        if (!payload.catv.package) delete payload.catv.package;
      }
      if (payload.broadband) {
        payload.broadband = {
          ...payload.broadband,
          houseNumber: form.address.houseNumber,
          additionalPlanIds: (payload.broadband.additionalPlanIds || []).filter(Boolean),
        };
        if (!payload.broadband.plan) delete payload.broadband.plan;
      }
      const { data } = await customerAPI.create(payload);
      toast.success(`Customer created: ${data.data.customerId || data.data.billingNumber}`);

      const staged = Object.entries(documents).filter(([, file]) => file);
      if (staged.length) {
        const results = await Promise.allSettled(staged.map(([key, file]) => {
          const label = DOCUMENT_TYPES.find(d => d.key === key)?.label || key;
          const fd = new FormData();
          fd.append('files', file);
          fd.append('label', label);
          return customerAPI.uploadDocuments(data.data._id, fd);
        }));
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed) toast.error(`${failed} document(s) failed to upload — you can retry from the customer's page`);
      }

      navigate(`/customers/${data.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const catvExpiry = useMemo(() => {
    const selectedPkg = packages.find(p => p._id === form.catv.package);
    const validityDays = selectedPkg?.validityDays || 30;
    const base = form.installationDate ? new Date(form.installationDate) : new Date();
    const exp = new Date(base);
    exp.setDate(exp.getDate() + validityDays);
    return { date: exp, days: validityDays };
  }, [form.catv.package, form.installationDate, packages]);

  const bbExpiry = useMemo(() => {
    const selectedPlan = plans.find(p => p._id === form.broadband.plan);
    const validityDays = selectedPlan?.validityDays || 30;
    const base = form.installationDate ? new Date(form.installationDate) : new Date();
    const exp = new Date(base);
    exp.setDate(exp.getDate() + validityDays);
    return { date: exp, days: validityDays };
  }, [form.broadband.plan, form.installationDate, plans]);
  const activeOpt = SERVICE_OPTIONS.find(o => o.key === form.customerType);
  const themeColor  = activeOpt?.color      || '#6b7280';
  const themeLight  = activeOpt?.colorLight || 'var(--cb-f3f4f6)';
  const themeMid    = activeOpt?.colorMid   || 'var(--cb-e5e7eb)';

  const themedCardStyle  = { borderTop: `3px solid ${themeColor}`, transition: 'border-color 0.3s' };
  const themedCardHeader = { borderBottom: `1px solid ${themeMid}`, background: themeLight, transition: 'background 0.3s, border-color 0.3s' };

  const submitBlocked = loading || !form.customerType
    || pppoeStatus === 'taken' || pppoeStatus === 'checking'
    || billingCheck.status === 'taken' || billingCheck.status === 'invalid' || billingCheck.status === 'checking'
    || (form.customerType === 'catv' && (!form.catv.accountNumber.trim() || acctStatus === 'taken' || acctStatus === 'checking'));

  const step = STEPS[stepIndex];
  const selectedPackage = packages.find(p => p._id === form.catv.package);
  const selectedPlan = plans.find(p => p._id === form.broadband.plan);

  // Billing Number — separate series per service, unique key within CableTV / within Broadband
  const renderBillingNumberField = () => (
    <div className="form-group">
      <label className="form-label">Billing Number</label>
      <div style={{ position: 'relative' }}>
        <input
          className="form-control font-mono"
          value={form.billingNumber}
          onChange={e => handleBillingNumberChange(e.target.value)}
          placeholder="Loading next number…"
          style={{
            paddingRight: 36,
            borderColor: (billingCheck.status === 'taken' || billingCheck.status === 'invalid') ? 'var(--cb-ef4444)'
              : billingCheck.status === 'ok' ? 'var(--cb-10b981)' : undefined,
          }}
        />
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
          {billingCheck.status === 'checking' && <Loader size={14} color="#9ca3af" />}
          {billingCheck.status === 'ok'       && <CheckCircle size={14} color="#10b981" />}
          {(billingCheck.status === 'taken' || billingCheck.status === 'invalid') && <XCircle size={14} color="#ef4444" />}
        </span>
      </div>
      {billingCheck.status === 'ok'      && <p style={{ fontSize: '0.75rem', color: 'var(--c-10b981)', marginTop: 3 }}>{billingCheck.message}</p>}
      {billingCheck.status === 'taken'   && <p style={{ fontSize: '0.75rem', color: 'var(--c-ef4444)', marginTop: 3 }}>{billingCheck.message}</p>}
      {billingCheck.status === 'invalid' && <p style={{ fontSize: '0.75rem', color: 'var(--c-ef4444)', marginTop: 3 }}>{billingCheck.message}</p>}
      {/* Number taken → offer the nearest free decimal slot on each side */}
      {billingCheck.status === 'taken' && (billingCheck.suggestions?.before || billingCheck.suggestions?.after) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Place this customer:</span>
          {billingCheck.suggestions.before && (
            <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
              onClick={() => handleBillingNumberChange(String(billingCheck.suggestions.before))}>
              Before {billingCheck.taken} → {billingCheck.suggestions.before}
            </button>
          )}
          {billingCheck.suggestions.after && (
            <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
              onClick={() => handleBillingNumberChange(String(billingCheck.suggestions.after))}>
              After {billingCheck.taken} → {billingCheck.suggestions.after}
            </button>
          )}
        </div>
      )}
      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3 }}>
        Pre-filled with the next number in the {form.customerType === 'catv' ? 'CableTV' : 'Broadband'} sequence — change it if needed. Decimals like 1.1 slot a customer between two numbers. Each service keeps its own series.
      </p>
    </div>
  );

  const subsectionHeader = (Icon, color, title) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 14px', paddingBottom: 8, borderBottom: `1px solid ${color}30` }}>
      {/* The raw hex feeds the underline tint; text and icon use its palette token so they read in dark. */}
      <Icon size={16} color={`var(--c-${color.slice(1).toLowerCase()})`} />
      <h4 style={{ color: `var(--c-${color.slice(1).toLowerCase()})`, margin: 0, fontSize: '.95rem' }}>{title}</h4>
    </div>
  );

  return (
    <div style={{ transition: 'all 0.3s' }}>
      {/* ── Page Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20,
        padding: '14px 18px', borderRadius: 12,
        background: activeOpt ? themeLight : 'var(--card-bg)',
        border: `1.5px solid ${activeOpt ? themeMid : 'var(--border)'}`,
        transition: 'background 0.3s, border-color 0.3s',
      }}>
        <button className="btn btn-secondary btn-sm" onClick={() => fromScan ? navigate('/scan') : navigate('/customers')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: activeOpt ? `${themeColor}18` : 'var(--cb-f3f4f6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {activeOpt
              ? <activeOpt.icon size={20} color={themeColor} strokeWidth={1.8} />
              : <UserPlus size={20} color="#9ca3af" strokeWidth={1.8} />}
          </div>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: activeOpt ? themeColor : 'var(--text)', transition: 'color 0.3s', margin: 0 }}>
              {activeOpt ? `Add ${activeOpt.label} Customer` : 'Add New Customer'}
            </h2>
            <p className="text-muted text-small" style={{ margin: 0 }}>
              {activeOpt ? activeOpt.desc : 'Select a customer type to get started'}
            </p>
          </div>
        </div>
      </div>

      {fromScan && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--cb-fefce8)', border: '1px solid var(--cb-fde68a)', borderRadius: 10, marginBottom: 20 }}>
          <ScanLine size={18} color="#ca8a04" />
          <div style={{ fontSize: '.88rem' }}>
            <strong style={{ color: 'var(--c-92400e)' }}>New STB scanned — not found in system</strong><br />
            <span style={{ color: 'var(--c-78350f)' }}>STB Serial <code style={{ background: 'var(--cb-fef3c7)', padding: '1px 6px', borderRadius: 4 }}>{prefilledStb}</code> pre-filled below.</span>
          </div>
        </div>
      )}

      {/* ── Step rail ── */}
      <div className="card mb-4" style={{ padding: '22px 26px', borderTop: `3px solid ${themeColor}`, transition: 'border-color 0.3s' }}>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: 13, left: 13, right: 13, height: 2, background: 'var(--border)' }} />
          <div style={{
            position: 'absolute', top: 13, left: 13, height: 2, background: themeColor,
            width: `calc(${(stepIndex / (STEPS.length - 1)) * 100}% * (100% - 26px) / 100%)`,
            maxWidth: 'calc(100% - 26px)', transition: 'width 200ms ease, background 0.3s',
          }} />
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isDone = i < stepIndex;
              const isActive = i === stepIndex;
              const isReachable = i <= furthestValid;
              return (
                <div
                  key={s.key}
                  onClick={() => goToStep(i)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
                    cursor: isReachable ? 'pointer' : 'default',
                    flex: i === 0 || i === STEPS.length - 1 ? '0 0 auto' : '1 1 0',
                  }}
                >
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: isDone || isActive ? themeColor : '#fff',
                    border: `2px solid ${isDone || isActive ? themeColor : 'var(--border)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}>
                    {isDone ? <Check size={13} color="#fff" /> : <Icon size={13} color={isActive ? '#fff' : '#9ca3af'} />}
                  </div>
                  <span style={{
                    fontSize: '.72rem', fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap',
                    color: isActive ? themeColor : isReachable ? 'var(--text)' : 'var(--text-muted)',
                  }}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Step content ── */}
      <div className="card mb-4" style={themedCardStyle}>
        <div className="card-header" style={themedCardHeader}>
          <h3 style={{ margin: 0 }}>Step {stepIndex + 1} of {STEPS.length} — {step.label}</h3>
        </div>
        <div className="card-body">

          {/* Step: Service Type */}
          {step.key === 'type' && (
            <div>
              <div style={{ display: 'flex', gap: 12 }}>
                {SERVICE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const isActive = form.customerType === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        set('customerType', opt.key);
                        // Broadband billing address usually differs from installation — expand it
                        if (opt.key === 'broadband') set('billingAddress.sameAsInstallation', false);
                        // Pure-broadband customers are billed DTD (per-visit), not on the
                        // auto-generated monthly cycle — policy as of 2026-08-01.
                        set('billing.durationType', opt.key === 'broadband' ? 'dtd' : 'regular');
                      }}
                      style={{
                        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 10, padding: '20px 12px', borderRadius: 12,
                        border: `2.5px solid ${isActive ? opt.color : '#e5e7eb'}`,
                        background: isActive ? opt.colorLight : 'var(--bg)',
                        cursor: 'pointer', transition: 'all 0.2s',
                        boxShadow: isActive ? `0 4px 16px ${opt.color}30` : 'none',
                        position: 'relative', overflow: 'hidden',
                      }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = opt.color; e.currentTarget.style.background = opt.colorLight; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = 'var(--cb-e5e7eb)'; e.currentTarget.style.background = 'var(--bg)'; } }}
                    >
                      {isActive && <span style={{ position: 'absolute', top: 8, right: 8, width: 10, height: 10, borderRadius: '50%', background: opt.color }} />}
                      <div style={{ width: 60, height: 60, borderRadius: 16, background: isActive ? `${opt.color}20` : 'var(--cb-f3f4f6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={30} color={isActive ? opt.color : '#9ca3af'} strokeWidth={1.6} />
                      </div>
                      <span style={{ fontWeight: isActive ? 700 : 500, fontSize: '0.92rem', color: isActive ? opt.color : 'var(--text-muted)' }}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {!form.customerType && (
                <p style={{ fontSize: '0.8rem', color: 'var(--c-f59e0b)', marginTop: 12, fontWeight: 500, textAlign: 'center' }}>
                  Select a type above to continue
                </p>
              )}
            </div>
          )}

          {/* Step: Personal Info */}
          {step.key === 'personal' && (
            <div>
              <div className="form-group">
                <label className="form-label">Full Name <span className="required">*</span></label>
                <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="Customer full name" />
              </div>
              <div className="form-group">
                <label className="form-label">Billing Name</label>
                <input className="form-control" value={form.billingName} onChange={e => set('billingName', e.target.value)} placeholder="Leave blank to use Full Name" />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Mobile <span className="required">*</span></label>
                  <input className="form-control" value={form.mobile} onChange={e => set('mobile', e.target.value)} required placeholder="Primary mobile" />
                </div>
                <div className="form-group">
                  <label className="form-label">Alternate Mobile</label>
                  <input className="form-control" value={form.alternateMobile} onChange={e => set('alternateMobile', e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label className="form-label">Installation Date <span className="required">*</span></label>
                <input type="date" className="form-control" value={form.installationDate} onChange={e => set('installationDate', e.target.value)} required />
              </div>

              <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 10 }}>
                  <input
                    type="checkbox"
                    checked={form.billingAddress.sameAsInstallation}
                    onChange={e => set('billingAddress.sameAsInstallation', e.target.checked)}
                  />
                  <span className="form-label" style={{ margin: 0 }}>Billing Address same as installation address</span>
                </label>
                {!form.billingAddress.sameAsInstallation && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Billing Address Line 1</label>
                      <input className="form-control" value={form.billingAddress.line1}
                        onChange={e => set('billingAddress.line1', e.target.value)} placeholder="Flat/House no., street" />
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input className="form-control" value={form.billingAddress.city}
                          onChange={e => set('billingAddress.city', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Pincode</label>
                        <input className="form-control" value={form.billingAddress.pincode}
                          onChange={e => set('billingAddress.pincode', e.target.value)} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step: Address */}
          {step.key === 'address' && (
            <div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">House / Flat No. <span className="required">*</span></label>
                  <input className="form-control" value={form.address.houseNumber} onChange={e => set('address.houseNumber', e.target.value)} required placeholder="e.g. A-101, Shop 3" />
                </div>
                <div className="form-group">
                  <label className="form-label">Society Name <span className="required">*</span></label>
                  <AddableSelect optionsKey={`societies_${form.customerType}`} value={form.address.area} onChange={v => set('address.area', v)} placeholder="Select society…" themeColor={themeColor} extraOptions={areaOptions} />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Latitude</label>
                  <input className="form-control" value={form.address.coordinates.lat} onChange={e => set('address.coordinates.lat', e.target.value)} placeholder="Optional" />
                </div>
                <div className="form-group">
                  <label className="form-label">Longitude</label>
                  <input className="form-control" value={form.address.coordinates.lng} onChange={e => set('address.coordinates.lng', e.target.value)} placeholder="Optional" />
                </div>
              </div>
              {(form.address.houseNumber || form.address.area) && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: themeLight, border: `1px solid ${themeMid}`, borderRadius: 8,
                  padding: '8px 14px', fontSize: '0.83rem', marginTop: 4,
                }}>
                  <div>
                    <span style={{ fontSize: '0.7rem', color: themeColor, fontWeight: 600, display: 'block', marginBottom: 1 }}>CUSTOMER ID</span>
                    <span style={{ color: themeColor, fontWeight: 700, fontFamily: 'monospace' }}>
                      {form.customerType === 'catv'
                        ? (form.billingNumber.trim() || 'Auto — from Billing No.')
                        : (form.address.houseNumber ? form.address.houseNumber.toUpperCase() : '—')}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '0.7rem', color: themeColor, fontWeight: 600, display: 'block', marginBottom: 1 }}>SOCIETY</span>
                    <span style={{ color: themeColor, fontWeight: 500 }}>{form.address.area || '—'}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Service Details (CATV and/or Broadband) */}
          {step.key === 'service' && (
            <div>
              {renderBillingNumberField()}

              {showCATV && (
                <div style={{ marginTop: 22 }}>
                  {subsectionHeader(Tv, '#1e3a8a', 'CableTV — Package & Hardware')}
                  <div className="form-group">
                    <label className="form-label">Package</label>
                    <SearchableSelect
                      value={form.catv.package}
                      onChange={v => set('catv.package', v)}
                      placeholder="Search package…"
                      options={packages.map(p => ({ value: p._id, label: `${p.displayName} — ₹${p.price}/mo` }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Additional Packages</label>
                    {form.catv.additionalPackageIds.map((id, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <SearchableSelect
                          style={{ flex: 1 }}
                          value={id}
                          onChange={v => set('catv.additionalPackageIds', form.catv.additionalPackageIds.map((x, xi) => xi === i ? v : x))}
                          placeholder="Search package…"
                          options={packages
                            .filter(p => p._id === id || (p._id !== form.catv.package && !form.catv.additionalPackageIds.includes(p._id)))
                            .map(p => ({ value: p._id, label: `${p.displayName} — ₹${p.price}/mo` }))}
                        />
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => set('catv.additionalPackageIds', form.catv.additionalPackageIds.filter((_, xi) => xi !== i))}>×</button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => set('catv.additionalPackageIds', [...form.catv.additionalPackageIds, ''])}>+ Add another package</button>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Node</label>
                    <AddableSelect optionsKey="nodes" searchable value={form.catv.node} onChange={v => set('catv.node', v)} placeholder="Select node…" themeColor="#1e3a8a" extraOptions={nodeOptions} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">STB Model</label>
                    <AddableSelect optionsKey="stbModels" searchable value={form.catv.stbModel} onChange={v => set('catv.stbModel', v)} placeholder="Select STB model…" themeColor="#1e3a8a" extraOptions={stbModelOptions} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">STB Serial</label>
                    <input className="form-control font-mono" value={form.catv.setTopBoxSerial} onChange={e => set('catv.setTopBoxSerial', e.target.value)} placeholder="Set-top box serial" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Entity Code</label>
                    <AddableSelect optionsKey="entityCodes" searchable value={form.catv.entityCode} onChange={v => set('catv.entityCode', v)} placeholder="Select entity code…" themeColor="#1e3a8a" extraOptions={entityCodeOptions} />
                  </div>
                  {/* Account Number — always entered manually, never auto-generated */}
                  <UniqueField
                    label="Account Number"
                    required
                    value={form.catv.accountNumber}
                    onChange={v => set('catv.accountNumber', v)}
                    checkFn={(v) => customerAPI.checkAccountNumber(v)}
                    placeholder="Enter account number (required)"
                    onStatus={setAcctStatus}
                  />
                </div>
              )}

              {showBB && (
                <div style={{ marginTop: 22 }}>
                  {subsectionHeader(Wifi, '#1e40af', 'Broadband — Plan & Connection')}
                  <div className="form-group">
                    <label className="form-label">Plan</label>
                    <SearchableSelect
                      value={form.broadband.plan}
                      onChange={v => set('broadband.plan', v)}
                      placeholder="Search plan…"
                      options={plans.map(p => ({
                        value: p._id,
                        label: `${p.displayName} — ${p.speed.download}/${p.speed.upload} Mbps — ₹${p.price} / ${p.validityDays || 30} days`,
                      }))}
                    />
                  </div>

                  {selectedPlan?.name === 'LCV_COMBO_NET_MONTHLY' && (
                    <div className="form-group">
                      <button
                        type="button"
                        className={`btn btn-sm ${form.broadband.autoRenew ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => set('broadband.autoRenew', !form.broadband.autoRenew)}
                      >
                        {form.broadband.autoRenew ? '✓ Auto-Renew ON' : 'Enable Auto-Renew'}
                      </button>
                      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        This plan is bundled free with the CATV combo — turning this on extends the
                        broadband validity by 30 days automatically each cycle, with no bill or manual renewal needed.
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Additional Plans</label>
                    {form.broadband.additionalPlanIds.map((id, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <SearchableSelect
                          style={{ flex: 1 }}
                          value={id}
                          onChange={v => set('broadband.additionalPlanIds', form.broadband.additionalPlanIds.map((x, xi) => xi === i ? v : x))}
                          placeholder="Search plan…"
                          options={plans
                            .filter(p => p._id === id || (p._id !== form.broadband.plan && !form.broadband.additionalPlanIds.includes(p._id)))
                            .map(p => ({ value: p._id, label: `${p.displayName} — ₹${p.price} / ${p.validityDays || 30} days` }))}
                        />
                        <button type="button" className="btn btn-secondary btn-sm"
                          onClick={() => set('broadband.additionalPlanIds', form.broadband.additionalPlanIds.filter((_, xi) => xi !== i))}>×</button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={() => set('broadband.additionalPlanIds', [...form.broadband.additionalPlanIds, ''])}>+ Add another plan</button>
                  </div>

                  {/* Router details */}
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Router Model</label>
                      <AddableSelect optionsKey="routerModels" value={form.broadband.routerModel} onChange={v => set('broadband.routerModel', v)} placeholder="Select router…" themeColor="#1e40af" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Router S/N</label>
                      <input className="form-control font-mono" value={form.broadband.routerSerial} onChange={e => set('broadband.routerSerial', e.target.value)} placeholder="Serial number" />
                    </div>
                  </div>

                  {/* ISP Name */}
                  <div className="form-group">
                    <ISPVlanSelect
                      ispName={form.broadband.ispName}
                      onChange={handleISPChange}
                      themeColor="#1e40af"
                    />
                  </div>

                  {/* PPPoE Username with uniqueness check */}
                  <div className="form-group">
                    <label className="form-label">PPPoE Username <span className="required">*</span></label>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-control font-mono"
                        value={form.broadband.username}
                        onChange={e => {
                          set('broadband.username', e.target.value);
                          setPppoeStatus('idle');
                          clearTimeout(window._pppoeDebounce);
                          if (!e.target.value.trim()) return;
                          setPppoeStatus('checking');
                          window._pppoeDebounce = setTimeout(async () => {
                            try {
                              const { data } = await customerAPI.checkPPPoE(e.target.value.trim());
                              setPppoeStatus(data.available ? 'ok' : 'taken');
                              if (!data.available) {
                                const c = data.conflict;
                                toast.error(`PPPoE used by ${c?.name} (${c?.customerId})`, { id: 'pppoe-check' });
                              }
                            } catch { setPppoeStatus('idle'); }
                          }, 600);
                        }}
                        placeholder="PPPoE / radius username"
                        style={{
                          paddingRight: 36,
                          borderColor: pppoeStatus === 'taken' ? 'var(--cb-ef4444)' : pppoeStatus === 'ok' ? 'var(--cb-10b981)' : undefined,
                        }}
                      />
                      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                        {pppoeStatus === 'checking' && <Loader size={14} color="#9ca3af" />}
                        {pppoeStatus === 'ok'       && <CheckCircle size={14} color="#10b981" />}
                        {pppoeStatus === 'taken'    && <XCircle size={14} color="#ef4444" />}
                      </span>
                    </div>
                    {pppoeStatus === 'ok'    && <p style={{ fontSize: '0.75rem', color: 'var(--c-10b981)', marginTop: 3 }}>Username available</p>}
                    {pppoeStatus === 'taken' && <p style={{ fontSize: '0.75rem', color: 'var(--c-ef4444)', marginTop: 3 }}>Username already in use</p>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Billing (Billing Details + Financial & Notes) */}
          {step.key === 'billing' && (
            <div>
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                {/* Row 1: Start Date | Additional Charge/Discount */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', borderBottom: '1px solid var(--border)' }}>
                  {/* Start Date */}
                  <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border)' }}>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: '.82rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>Start Date</label>
                      <input type="date" className="form-control" value={form.billing.startDate}
                        onChange={e => set('billing.startDate', e.target.value)} />
                    </div>
                    {form.billing.startDate && (
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: '.82rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                          Till {new Date(new Date(form.billing.startDate) - 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })} opening balance is:
                        </label>
                        <input type="number" className="form-control" value={form.billing.openingBalance}
                          onChange={e => set('billing.openingBalance', parseFloat(e.target.value) || 0)}
                          style={{ maxWidth: 140 }} placeholder="₹ 0" />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 20, marginBottom: 8 }}>
                      {['outstanding', 'advance'].map(t => (
                        <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer', fontWeight: form.billing.openingBalanceType === t ? 600 : 400 }}>
                          <input type="radio" name="openingBalanceType" value={t}
                            checked={form.billing.openingBalanceType === t}
                            onChange={() => set('billing.openingBalanceType', t)} />
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </label>
                      ))}
                    </div>
                    {form.billing.startDate && (
                      <p style={{ fontSize: '.75rem', color: 'var(--c-dc2626)', margin: 0 }}>
                        Automatic Bills will be generated from {new Date(form.billing.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} and ₹{form.billing.openingBalance} will be added to bill
                      </p>
                    )}
                  </div>

                  {/* Additional Charge/Discount */}
                  <div style={{ padding: '16px 20px' }}>
                    <label style={{ fontSize: '.82rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>Additional Charge/Discount per month</label>
                    <input type="number" className="form-control" value={form.billing.monthlyAdjustment}
                      onChange={e => set('billing.monthlyAdjustment', parseFloat(e.target.value) || 0)}
                      style={{ maxWidth: 140, marginBottom: 10 }} placeholder="₹ 0" />
                    <div style={{ display: 'flex', gap: 20, marginBottom: 8 }}>
                      {[['charge', 'Additional Charge'], ['discount', 'Discount']].map(([val, label]) => (
                        <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer', fontWeight: form.billing.monthlyAdjustmentType === val ? 600 : 400 }}>
                          <input type="radio" name="monthlyAdjustmentType" value={val}
                            checked={form.billing.monthlyAdjustmentType === val}
                            onChange={() => set('billing.monthlyAdjustmentType', val)} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <p style={{ fontSize: '.75rem', color: 'var(--c-dc2626)', margin: 0 }}>
                      ₹{form.billing.monthlyAdjustment} will be {form.billing.monthlyAdjustmentType === 'discount' ? 'deducted from' : 'added to'} every bill
                    </p>
                  </div>
                </div>

                {/* Row 2: Bill Duration | Bill Type */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', borderBottom: '1px solid var(--border)' }}>
                  {/* Bill Duration */}
                  <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border)' }}>
                    <label style={{ fontSize: '.82rem', fontWeight: 600, display: 'block', marginBottom: 10 }}>Bill Duration</label>
                    <div style={{ display: 'flex', gap: 24, marginBottom: 8 }}>
                      {[['regular', 'End of every month'], ['dtd', 'DTD'], ['amp', 'AMP']].map(([val, label]) => (
                        <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer', fontWeight: form.billing.durationType === val ? 600 : 400 }}>
                          <input type="radio" name="durationType" value={val}
                            checked={form.billing.durationType === val}
                            onChange={() => set('billing.durationType', val)} />
                          {label}
                        </label>
                      ))}
                    </div>
                    {form.billing.durationType === 'dtd' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <select
                          value={form.billing.durationDays}
                          onChange={e => set('billing.durationDays', parseInt(e.target.value))}
                          style={{
                            padding: '4px 8px', borderRadius: 6, fontSize: '.83rem',
                            border: '1.5px solid var(--cb-e5e7eb)', outline: 'none',
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (i + 1) * 30).map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <span style={{ fontSize: '.82rem', color: 'var(--c-6b7280)' }}>days</span>
                      </div>
                    )}
                    <p style={{ fontSize: '.75rem', color: 'var(--c-dc2626)', margin: 0 }}>
                      {form.billing.durationType === 'dtd' && `Customer will be billed every ${form.billing.durationDays} days. No automatic monthly bill.`}
                      {form.billing.durationType === 'regular' && 'Auto-renews — a bill is generated automatically at the end of every month, skipping any month already collected in advance.'}
                      {form.billing.durationType === 'amp' && 'Auto Monthly Payment — paid by Laxmi Cable Vision. Not included in the automatic monthly bill generation. This customer never receives a bill-generated or payment-collected WhatsApp message.'}
                    </p>
                  </div>

                  {/* Bill Type */}
                  <div style={{ padding: '16px 20px' }}>
                    <label style={{ fontSize: '.82rem', fontWeight: 600, display: 'block', marginBottom: 10 }}>Bill Type</label>
                    <div style={{ display: 'flex', gap: 24, marginBottom: 8 }}>
                      {[['postpaid', 'Postpaid'], ['prepaid', 'Prepaid']].map(([val, label]) => (
                        <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer', fontWeight: form.billing.billType === val ? 600 : 400 }}>
                          <input type="radio" name="billType" value={val}
                            checked={form.billing.billType === val}
                            onChange={() => set('billing.billType', val)} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <p style={{ fontSize: '.75rem', color: 'var(--c-dc2626)', margin: 0 }}>
                      {form.billing.billType === 'prepaid' ? 'Advance Billing.' : 'Bill generated after service period.'}
                    </p>
                  </div>
                </div>

                {/* Row 3: GST Type */}
                <div style={{ padding: '16px 20px' }}>
                  <label style={{ fontSize: '.82rem', fontWeight: 600, display: 'block', marginBottom: 10 }}>GST Type</label>
                  <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                    {[
                      ['no_gst', 'No GST'],
                      ['cgst_sgst', 'CGST + SGST — Customer from same state'],
                      ['igst', 'IGST — Customer from different state'],
                    ].map(([val, label]) => (
                      <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer', fontWeight: form.billing.gstType === val ? 600 : 400 }}>
                        <input type="radio" name="gstType" value={val}
                          checked={form.billing.gstType === val}
                          onChange={() => set('billing.gstType', val)} />
                        {label}
                      </label>
                    ))}
                  </div>
                  {form.billing.gstType !== 'no_gst' && (
                    <div className="form-group" style={{ marginTop: 12, maxWidth: 320 }}>
                      <label className="form-label">GST No.</label>
                      <input className="form-control" value={form.billing.gstNumber} onChange={e => set('billing.gstNumber', e.target.value)} placeholder="GSTIN" />
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 22 }}>
                {subsectionHeader(Receipt, '#0f766e', 'Financial & Notes')}
                <div className="form-group">
                  <label className="form-label">Security Deposit (₹)</label>
                  <input type="number" className="form-control" value={form.securityDeposit} onChange={e => set('securityDeposit', parseInt(e.target.value) || 0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px', background: 'var(--bg)', borderRadius: 6, marginTop: 8 }}>
                  <input type="checkbox" id="portalLogin" checked={form.createPortalLogin} onChange={e => set('createPortalLogin', e.target.checked)} />
                  <label htmlFor="portalLogin" style={{ fontSize: '.85rem', cursor: 'pointer' }}>
                    Create customer portal login (default password: last 4 digits of mobile)
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step: Documents — optional; whichever files are attached get uploaded
              right after the customer record is created. */}
          {step.key === 'documents' && (
            <div>
              {DOCUMENT_TYPES.filter(d =>
                (showCATV && d.services.includes('catv')) || (showBB && d.services.includes('broadband'))
              ).map(d => (
                <div key={d.key} className="form-group">
                  <label className="form-label">{d.label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                      <Paperclip size={13} /> {documents[d.key] ? 'Replace file' : 'Choose file'}
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        style={{ display: 'none' }}
                        onChange={e => setDocuments(prev => ({ ...prev, [d.key]: e.target.files?.[0] || null }))}
                      />
                    </label>
                    {documents[d.key] && (
                      <>
                        <span style={{ fontSize: '.82rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                          {documents[d.key].name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setDocuments(prev => ({ ...prev, [d.key]: null }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}
                          title="Remove"
                        >
                          <X size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step: Review */}
          {step.key === 'review' && (
            <div>
              <ReviewGroup title="Service Type" onEdit={() => goToStep(0)}>
                <ReviewRow label="Customer Type" value={activeOpt?.label || '—'} />
              </ReviewGroup>

              <ReviewGroup title="Personal Info" onEdit={() => goToStep(1)}>
                <ReviewRow label="Name" value={form.name || '—'} />
                {form.billingName && <ReviewRow label="Billing Name" value={form.billingName} />}
                <ReviewRow label="Mobile" value={form.mobile || '—'} />
                {form.alternateMobile && <ReviewRow label="Alternate Mobile" value={form.alternateMobile} />}
                {form.email && <ReviewRow label="Email" value={form.email} />}
                <ReviewRow label="Installation Date" value={form.installationDate || '—'} />
                <ReviewRow label="Billing Address" value={form.billingAddress.sameAsInstallation ? 'Same as installation' : (form.billingAddress.line1 || '—')} />
              </ReviewGroup>

              <ReviewGroup title="Address" onEdit={() => goToStep(2)}>
                <ReviewRow label="House / Flat No." value={form.address.houseNumber || '—'} />
                <ReviewRow label="Society" value={form.address.area || '—'} />
                {(form.address.coordinates.lat || form.address.coordinates.lng) && (
                  <ReviewRow label="Coordinates" value={`${form.address.coordinates.lat}, ${form.address.coordinates.lng}`} />
                )}
              </ReviewGroup>

              <ReviewGroup title="Service Details" onEdit={() => goToStep(3)}>
                <ReviewRow label="Billing Number" value={form.billingNumber || '—'} />
                {showCATV && <>
                  <ReviewRow label="CableTV Package" value={selectedPackage?.displayName || selectedPackage?.name || '—'} />
                  {form.catv.additionalPackageIds.filter(Boolean).length > 0 && (
                    <ReviewRow label="Additional Packages" value={String(form.catv.additionalPackageIds.filter(Boolean).length)} />
                  )}
                  <ReviewRow label="STB Serial" value={form.catv.setTopBoxSerial || '—'} />
                  <ReviewRow label="Account Number" value={form.catv.accountNumber || '—'} />
                  <ReviewRow label="CableTV Expiry" value={fmtExpiryShort(catvExpiry.date)} />
                </>}
                {showBB && <>
                  <ReviewRow label="Broadband Plan" value={selectedPlan?.displayName || selectedPlan?.name || '—'} />
                  {form.broadband.additionalPlanIds.filter(Boolean).length > 0 && (
                    <ReviewRow label="Additional Plans" value={String(form.broadband.additionalPlanIds.filter(Boolean).length)} />
                  )}
                  <ReviewRow label="PPPoE Username" value={form.broadband.username || '—'} />
                  <ReviewRow label="Broadband Expiry" value={fmtExpiryShort(bbExpiry.date)} />
                  {selectedPlan?.name === 'LCV_COMBO_NET_MONTHLY' && (
                    <ReviewRow label="Auto-Renew" value={form.broadband.autoRenew ? 'ON' : 'Off'} />
                  )}
                </>}
              </ReviewGroup>

              <ReviewGroup title="Billing" onEdit={() => goToStep(4)}>
                <ReviewRow label="Start Date" value={form.billing.startDate || '—'} />
                <ReviewRow label="Opening Balance" value={`₹${form.billing.openingBalance} (${form.billing.openingBalanceType})`} />
                <ReviewRow label="Monthly Adjustment" value={`₹${form.billing.monthlyAdjustment} (${form.billing.monthlyAdjustmentType})`} />
                <ReviewRow label="Bill Duration" value={
                  form.billing.durationType === 'dtd' ? `Every ${form.billing.durationDays} days`
                  : form.billing.durationType === 'amp' ? 'AMP — auto-paid by Laxmi Cable Vision'
                  : 'End of every month'
                } />
                <ReviewRow label="Bill Type" value={form.billing.billType} />
                <ReviewRow label="GST" value={form.billing.gstType === 'no_gst' ? 'No GST' : `${form.billing.gstType} (${form.billing.gstNumber || '—'})`} />
                <ReviewRow label="Security Deposit" value={`₹${form.securityDeposit}`} />
                <ReviewRow label="Portal Login" value={form.createPortalLogin ? 'Will be created' : 'Not created'} />
              </ReviewGroup>

              <ReviewGroup title="Documents" onEdit={() => goToStep(5)} last>
                {Object.entries(documents).filter(([, f]) => f).length === 0
                  ? <ReviewRow label="Attached" value="None" />
                  : Object.entries(documents).filter(([, f]) => f).map(([key, file]) => (
                    <ReviewRow key={key} label={DOCUMENT_TYPES.find(d => d.key === key)?.label || key} value={file.name} />
                  ))}
              </ReviewGroup>
            </div>
          )}

        </div>
      </div>

      {/* ── Footer nav ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <button type="button" className="btn btn-secondary" onClick={stepIndex === 0 ? () => navigate('/customers') : goBack}>
          <ChevronLeft size={15} /> {stepIndex === 0 ? 'Cancel' : 'Back'}
        </button>
        {step.key === 'review' ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={submitBlocked}
            onClick={handleSubmit}
            style={activeOpt ? { background: activeOpt.color, borderColor: activeOpt.color, transition: 'background 0.3s, border-color 0.3s' } : {}}
          >
            <UserPlus size={15} /> {loading ? 'Creating...' : activeOpt ? `Create ${activeOpt.label} Customer` : 'Create Customer'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={goNext}
            style={activeOpt ? { background: activeOpt.color, borderColor: activeOpt.color, transition: 'background 0.3s, border-color 0.3s' } : {}}
          >
            Continue <ChevronRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

function fmtExpiryShort(date) {
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ReviewGroup({ title, children, onEdit, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : 20, paddingBottom: last ? 0 : 20, borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: '.9rem' }}>{title}</span>
        <button type="button" onClick={onEdit} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: '.82rem', cursor: 'pointer', padding: 0 }}>
          Edit
        </button>
      </div>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', gap: 12 }}>
      <span style={{ fontSize: '.83rem', color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '.83rem', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
